import { useMemo, useRef, useState } from "react";

import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "@impero-com/common/utils/i18n";
import {
    Button,
    Alert,
    FormLabel,
    TableToolbar,
    ToolbarSpacer,
    TSTAdjustView,
    TSTPagination,
    TSTTable,
} from "@impero-com/ui";
import * as Sentry from "@sentry/react";
import {
    Control,
    FieldErrors,
    useFieldArray,
    UseFormTrigger,
} from "react-hook-form";

import type {
    FieldForSampleConclusionFieldKindAndSampleConclusionAnswerKind,
    SavedBlob,
    TestingActivitySample,
    UpdatableTestingActivity,
} from "api/generated/model";

import { usePatchTestingActivityMutation } from "../../queries/usePatchTestingActivityMutation";
import { ActivitySample, SAMPLES_KEY, TestingActivityForm } from "../../schema";
import { ControlSample } from "../../types";
import { AddSamplesTable } from "../AddSamplesTable/AddSamplesTable";
import { SampleDrawer } from "../SampleDrawer/SampleDrawer";

import * as theme from "./SelectedSamplesTable.css";
import { useSelectedSamplesTable } from "./useSelectedSamplesTable";
import { ViewedIndicator } from "./ViewedIndicator";

interface SelectedSamplesTableProps {
    control: Control<TestingActivityForm>;
    controlSamples: ControlSample[];
    trigger: UseFormTrigger<TestingActivityForm>;
    errors: FieldErrors<ActivitySample[]> | undefined;
    sampleConclusionFields: FieldForSampleConclusionFieldKindAndSampleConclusionAnswerKind[];
    testingActivityId: string;
    readOnly: boolean;
    controlActivitiesSamples: Record<string, TestingActivitySample>;
    savedBlobs: SavedBlob[];
    requestedRandomSampleCount: number;
}

export function SelectedSamplesTable({
    control,
    controlSamples,
    trigger,
    errors,
    sampleConclusionFields,
    testingActivityId,
    readOnly,
    controlActivitiesSamples,
    savedBlobs,
    requestedRandomSampleCount,
}: SelectedSamplesTableProps) {
    const { t } = useTranslation();
    const [addSamplesOpen, setAddSamplesOpen] = useState(false);
    const {
        fields: samplesFields,
        remove,
        append,
        insert,
    } = useFieldArray({ control, name: SAMPLES_KEY });
    const { mutateAsync: mutatePatchActivity } =
        usePatchTestingActivityMutation();

    const randomlySelectedSampleCount = controlSamples.filter(
        (sample) => sample.isRandom
    ).length;

    const hasSampleDiscrepancy =
        randomlySelectedSampleCount < requestedRandomSampleCount;

    const controlSamplesInForm: ControlSample[] = useMemo(
        () =>
            samplesFields
                .filter((sampleField) => {
                    // sc-30721 In Safari it is possible to get a sampleField without activityId after a removal.
                    if (!sampleField.activityId) {
                        Sentry.captureMessage(
                            "Sample without activityId, this is a known bug in Safari",
                            (scope) => {
                                return scope.setContext("samples", {
                                    samplesFields,
                                    controlSamples,
                                });
                            }
                        );
                        return false;
                    }
                    return true;
                })
                .map((sampleField) => {
                    const controlSample = controlSamples.find(
                        (sample) => sample.activityId === sampleField.activityId
                    );
                    if (controlSample === undefined) {
                        throw new Error(
                            `Sample with id ${sampleField.activityId} not found`
                        );
                    }
                    return controlSample;
                }),
        [samplesFields, controlSamples]
    );

    const handleRemove = async (activityId: string) => {
        const idxToRemove = samplesFields.findIndex(
            (sample) => sample.activityId === activityId
        );
        const fieldClone = structuredClone(samplesFields[idxToRemove]);
        remove(idxToRemove);

        const data: UpdatableTestingActivity = {
            type: "responsible",
            samples: {
                [activityId]: {
                    type: "Remove",
                },
            },
            controlConclusionFields: undefined,
        };
        try {
            await mutatePatchActivity({ testingActivityId, data });
        } catch {
            insert(idxToRemove, fieldClone);
        }
    };

    const getSampleFieldIndex = (activityId: string) => {
        return samplesFields.findIndex(
            (sample) => sample.activityId === activityId
        );
    };

    const renderViewedIndicator = (sample: ControlSample) => (
        <ViewedIndicator
            control={control}
            sampleFieldIndex={getSampleFieldIndex(sample.activityId)}
            disabled={readOnly}
        />
    );

    const [selectedDrawerSampleId, setSelectedDrawerSampleId] = useState<
        string | null
    >(null);
    const [sampleDrawerOpen, setSampleDrawerOpen] = useState<boolean>(false);

    const handleSampleClick = (sampleId: string | null) => {
        setSelectedDrawerSampleId(sampleId);
        setSampleDrawerOpen(true);
    };

    const { table } = useSelectedSamplesTable({
        data: controlSamplesInForm,
        renderViewedIndicator,
        setOpenSampleDrawerId: handleSampleClick,
        sampleErrors: errors,
        getSampleFieldIndex,
        sampleConclusionFields,
        control,
        blobs: savedBlobs,
    });

    const availableSamples = controlSamples.filter(
        (sample) =>
            !samplesFields.some(
                (field) => field.activityId === sample.activityId
            )
    );

    const activeDrawerSample = controlSamples.find(
        (sample) => sample.activityId === selectedDrawerSampleId
    );

    const tableRef = useRef<HTMLTableElement>(null);

    return (
        <>
            <SampleDrawer
                sample={activeDrawerSample}
                key={activeDrawerSample?.activityId}
                sampleFieldIndex={
                    activeDrawerSample
                        ? getSampleFieldIndex(activeDrawerSample.activityId)
                        : -1
                }
                onRemove={() =>
                    activeDrawerSample &&
                    void handleRemove(activeDrawerSample.activityId)
                }
                answers={
                    activeDrawerSample
                        ? controlActivitiesSamples[
                              activeDrawerSample.activityId
                          ]?.answers || {}
                        : {}
                }
                fields={sampleConclusionFields}
                control={control}
                trigger={trigger}
                readOnly={readOnly}
                savedBlobs={savedBlobs}
                onClose={() => setSampleDrawerOpen(false)}
                open={sampleDrawerOpen}
            />
            <div className={theme.tableButtonContainer}>
                <FormLabel>
                    {t("ControlTestingActivityView_SamplesTable_Label")}
                </FormLabel>
                {hasSampleDiscrepancy && (
                    <Alert
                        showIcon
                        type="warning"
                        message={t(
                            "ControlTestingActivityView_SamplesTable_Alert",
                            {
                                setSampleSize: requestedRandomSampleCount,
                                actualSampleSize: randomlySelectedSampleCount,
                            }
                        )}
                    />
                )}
                <div>
                    <TSTTable
                        tableRef={tableRef}
                        table={table}
                        noResultsFallback={t("NoResults")}
                        noSourceRowsFallback={t("NoResults")}
                        toolbar={
                            <TableToolbar>
                                <ToolbarSpacer />
                                <TSTAdjustView
                                    disabled={controlSamplesInForm.length === 0}
                                    table={table}
                                    columnsLabel={t("AdjustView_ColumnsHeader")}
                                    reorderHintLabel={t(
                                        "AdjustView_DragToReorderHint"
                                    )}
                                    resetLabel={t("AdjustView_Reset")}
                                    pinLabel={t("AdjustView_PinColumn")}
                                    unpinLabel={t("AdjustView_UnpinColumn")}
                                    containerRef={tableRef}
                                >
                                    {t("ControlResult_AdjustView")}
                                </TSTAdjustView>
                            </TableToolbar>
                        }
                    />
                    <TSTPagination
                        tableElement={tableRef}
                        table={table}
                        pageLabel={t("Pagination_Page")}
                        variant="inline"
                        getRowCountLabel={(shown, count) =>
                            t("Pagination_RowCount", { shown, count })
                        }
                        getSinglePageRowCountLabel={(count) =>
                            t("Pagination_RowCountSinglePage", { count })
                        }
                    />
                </div>

                {!readOnly && (
                    <div>
                        <Button
                            icon={<PlusOutlined />}
                            variant="link"
                            onClick={() => setAddSamplesOpen(true)}
                        >
                            {t(
                                "ControlTestingActivityView_SamplesTable_AddSamplesButton"
                            )}
                        </Button>
                    </div>
                )}
            </div>
            <AddSamplesTable
                open={addSamplesOpen}
                onClose={() => setAddSamplesOpen(false)}
                controlSamples={availableSamples}
                append={append}
                sampleConclusionFields={sampleConclusionFields}
            />
        </>
    );
}
